import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useLoader, useThree, useFrame } from '@react-three/fiber'
import { Text } from '@react-three/drei'
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js'
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js'
import Cell from './Cell'
import { statusToColor, STATUS } from '../../utils/status'

export default function Svg({ url, regions, onHover, onSelect, selectedRegion, hoveredRegion, activeStage, hoveredStage, mapControlsRef, backgroundUrl, backgroundSvgUrl, onShapeData, onReady, stageLockInfo = {}, isTouch = false, onShapesMap, initialZoomToStage, isAdminMode = false, selectedGroup = null, hoveredGroup = null, activeTab = 'available', statusColors = null, soldStages = null }) {
  const { paths } = useLoader(SVGLoader, url)
  const backgroundTexture = backgroundUrl ? useLoader(THREE.TextureLoader, backgroundUrl) : null

  const overlayIsSvg = useMemo(() => {
    if (!backgroundSvgUrl) return false
    return backgroundSvgUrl.toLowerCase().includes('.svg')
  }, [backgroundSvgUrl])

  const overlaySvgArray = useLoader(
    SVGLoader,
    overlayIsSvg && backgroundSvgUrl ? [backgroundSvgUrl] : []
  )
  const overlayTextureArray = useLoader(
    THREE.TextureLoader,
    !overlayIsSvg && backgroundSvgUrl ? [backgroundSvgUrl] : []
  )
  const overlaySvg = overlaySvgArray && overlaySvgArray.length > 0 ? overlaySvgArray[0] : null
  const overlayTexture = overlayTextureArray && overlayTextureArray.length > 0 ? overlayTextureArray[0] : null
  const shapes = useMemo(() => paths.flatMap((p) => p.toShapes(true).map((shape) => ({ shape, color: p.color, fillOpacity: p.userData?.style?.fillOpacity }))), [paths])
  const getStatusColor = useCallback((status) => statusToColor(status, statusColors), [statusColors])

  const getRegionForIndex = (idx) => {
    if (!regions) return null
    const byStable = regions.find((r) => r.svgPathId === `path-${idx}-shape-0`)
    if (byStable) return byStable
    const byId = regions.find((r) => r.svgPathId === `path-${idx}`)
    if (byId) return byId
    if (idx >= 0 && idx < regions.length) return regions[idx]
    return null
  }

  // Get group color from database (customProps) or localStorage or generate from hash
  // Prioritizes database colors so they sync across devices
  const getGroupColor = (groupName, region = null, subGroupName = null) => {
    if (!groupName) return '#6b7280'
    
    // Handle combined format "group::subGroup" for backward compatibility
    let actualGroupName = groupName
    let actualSubGroupName = subGroupName
    if (typeof groupName === 'string' && groupName.includes('::') && !subGroupName) {
      const parts = groupName.split('::')
      actualGroupName = parts[0]
      actualSubGroupName = parts[1] || null
    }
    
    // First, try to get color from region's customProps if available (database)
    if (region) {
      if (actualSubGroupName && region.customProps?.subGroup === actualSubGroupName && region.customProps?.subGroupColor) {
        return region.customProps.subGroupColor
      }
      if (!actualSubGroupName && region.customProps?.group === actualGroupName && region.customProps?.groupColor) {
      return region.customProps.groupColor
      }
    }
    
    // Try to find color in any region with this group/sub-group (from database)
    // This ensures colors sync across devices even if the current region doesn't have it
    if (regions && Array.isArray(regions)) {
      const matchingRegion = regions.find(r => {
        if (actualSubGroupName) {
          return r.customProps?.group === actualGroupName && r.customProps?.subGroup === actualSubGroupName
        } else {
          return r.customProps?.group === actualGroupName
        }
      })
      
      if (matchingRegion) {
        if (actualSubGroupName && matchingRegion.customProps?.subGroupColor) {
          return matchingRegion.customProps.subGroupColor
        }
        if (!actualSubGroupName && matchingRegion.customProps?.groupColor) {
          return matchingRegion.customProps.groupColor
        }
      }
    }
    
    // Fallback to localStorage (for backward compatibility)
    const groupNameStr = String(actualGroupName)
    const key = actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName
    const variations = [
      key,                    // Combined key
      groupNameStr,           // Original
      groupNameStr.trim(),    // Trimmed
      groupNameStr.trim().toLowerCase(), // Lowercase trimmed
      groupNameStr.toLowerCase()         // Lowercase original
    ]
    
    // Remove duplicates
    const uniqueVariations = [...new Set(variations)]
    
    // Try each variation
    for (const variation of uniqueVariations) {
      const stored = localStorage.getItem(`group_color_${variation}`)
      if (stored) {
        return stored
      }
    }
    
    // If not found, try to find any key that starts with group_color_ and check all
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('group_color_')) {
          const storedGroupName = key.replace('group_color_', '')
          // Check if stored group name matches (case-insensitive, trimmed)
          if (storedGroupName.trim().toLowerCase() === (actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName).trim().toLowerCase()) {
            const storedColor = localStorage.getItem(key)
            if (storedColor) {
              return storedColor
            }
          }
        }
      }
    } catch (e) {
      // Ignore localStorage access errors
    }
    
    // Generate color from hash (same palette as MapEditor)
    const groupColorPalette = [
      '#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6',
      '#0ea5e9', '#22c55e', '#eab308', '#f97316', '#14b8a6',
      '#a855f7', '#f43f5e'
    ]
    let hash = 0
    const nameToHash = actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName
    const nameToHashStr = String(nameToHash).trim()
    for (let i = 0; i < nameToHashStr.length; i++) {
      hash = ((hash << 5) - hash) + nameToHashStr.charCodeAt(i)
    }
    const colorIndex = Math.abs(hash) % groupColorPalette.length
    return groupColorPalette[colorIndex]
  }

  const enriched = useMemo(() => {
    return shapes.map((s, idx) => {
      const region = getRegionForIndex(idx)
      let baseColor = '#D3D3D3'
      let isLocked = false
      
      if (region) {
        const hasDetails = Boolean(region.name || region.lotNumber || region.lotSize || region.price || region.notes)
        const stageKey = (region?.name && String(region.name).trim()) || 'Ungrouped'
        const stageInfo = stageLockInfo && stageLockInfo[stageKey]
        isLocked = stageInfo ? (stageInfo.isLocked || false) : false
        
        if (isLocked) {
          baseColor = '#9ca3af' // Gray color for locked stages
        } else {
          // CRITICAL: When no stage is active, always show status colors (not group colors)
          // This applies to both normal and admin modes
          if (!activeStage) {
            // No stage active - show status colors for all lots (including sold lots)
            baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
          }
          // In admin mode, show status colors by default, group colors only on hover
          else if (isAdminMode && activeStage) {
            const regionStageName = (region.name && String(region.name).trim()) || 'Ungrouped'
            
            // CRITICAL: Lots from other stages should always be grayed out
            if (regionStageName !== activeStage) {
              baseColor = '#D3D3D3'
            }
            // Priority 1: When a group/sub-group is selected, ignore ALL hover - show colors based on selection
            // CRITICAL: When selectedGroup is set, completely ignore hoveredRegion and hoveredGroup
            if (selectedGroup) {
              // Group/sub-group is selected - show sub-group colors for sold lots in selected group, red for other sold lots
              // Colors should NOT change when hovering over lots
              if (hasDetails && region.status === STATUS.SOLD) {
                // Check if this sold lot belongs to the selected group
                const regionStageName = (region.name && String(region.name).trim()) || 'Ungrouped'
                const belongsToSelectedGroup = regionStageName === selectedGroup.stageName && 
                                              region.customProps?.group === selectedGroup.groupName
                
                if (belongsToSelectedGroup) {
                  // Sold lot belongs to selected group - show sub-group color if it has one, otherwise group color
                  if (region.customProps?.subGroup) {
                    baseColor = getGroupColor(`${region.customProps.group}::${region.customProps.subGroup}`, region)
                  } else if (region.customProps?.group) {
                  baseColor = getGroupColor(region.customProps.group, region)
                  } else {
                    baseColor = getStatusColor(STATUS.SOLD) // Red for ungrouped sold lots
                  }
                } else {
                  // Sold lot from other group - show red color
                  baseColor = getStatusColor(STATUS.SOLD)
                }
              } else {
                // Non-sold lots show status colors
                baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
              }
            }
            // Priority 2: If a specific lot is hovered (only when no group is selected)
            else if (!selectedGroup && hoveredRegion && region && hoveredRegion.id === region.id) {
              // When hovering over a specific lot, show status color for that lot
              baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
            }
            // Priority 3: If a specific lot is hovered, keep status colors for other lots (don't dim)
            else if (!selectedGroup && hoveredRegion && region && hoveredRegion.id !== region.id && regionStageName === activeStage) {
              // When hovering over a specific lot, keep status colors for other lots
              baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
            }
            // Default: Show status colors when no hover/selection
            else {
              // Show status colors by default
              baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
            }
          } else {
            // Normal mode with stage active - show status colors
            baseColor = hasDetails ? getStatusColor(region.status) : '#D3D3D3'
          }
        }
      } else {
        baseColor = '#D3D3D3'
      }
      const stageKey = (region?.name && String(region.name).trim()) || 'Ungrouped'
      return { ...s, baseColor, region, stageKey, isLocked }
    })
  }, [shapes, regions, stageLockInfo, isAdminMode, selectedGroup, hoveredGroup, activeStage, activeTab, hoveredRegion])

  // Create a map of region ID to shape for easy lookup
  const regionShapeMap = useMemo(() => {
    const map = new Map()
    enriched.forEach((item, idx) => {
      if (item.region) {
        map.set(item.region.id, item.shape)
      }
    })
    return map
  }, [enriched])

  const stageBounds = useMemo(() => {
    const bounds = new Map()
    enriched.forEach(item => {
      if (!item.region || !item.stageKey || !item.shape) return
      const points = item.shape.getPoints()
      if (!points || points.length === 0) return
      let stageBound = bounds.get(item.stageKey)
      if (!stageBound) {
        stageBound = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
        bounds.set(item.stageKey, stageBound)
      }
      points.forEach(point => {
        stageBound.minX = Math.min(stageBound.minX, point.x)
        stageBound.minY = Math.min(stageBound.minY, point.y)
        stageBound.maxX = Math.max(stageBound.maxX, point.x)
        stageBound.maxY = Math.max(stageBound.maxY, point.y)
      })
    })
    return bounds
  }, [enriched])

  const soldTexture = useMemo(() => {
    const width = 1024
    const height = 512
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.clearRect(0, 0, width, height)
      ctx.save()
      ctx.fillStyle = '#ffffff'
      const radius = 80
      ctx.beginPath()
      ctx.moveTo(radius, 0)
      ctx.lineTo(width - radius, 0)
      ctx.quadraticCurveTo(width, 0, width, radius)
      ctx.lineTo(width, height - radius)
      ctx.quadraticCurveTo(width, height, width - radius, height)
      ctx.lineTo(radius, height)
      ctx.quadraticCurveTo(0, height, 0, height - radius)
      ctx.lineTo(0, radius)
      ctx.quadraticCurveTo(0, 0, radius, 0)
      ctx.closePath()
      ctx.fill()
      
      ctx.fillStyle = '#E30613'
      ctx.font = 'bold 190px "Helvetica", "Arial", sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('SOLD OUT', width / 2, height / 2 + 10)
      ctx.restore()
    }
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.needsUpdate = true
    texture.anisotropy = 8
    return texture
  }, [])
  const soldTextureAspect = useMemo(() => {
    if (!soldTexture || !soldTexture.image) return 2
    return (soldTexture.image.width || 1024) / (soldTexture.image.height || 512)
  }, [soldTexture])

  useEffect(() => {
    return () => {
      soldTexture.dispose()
    }
  }, [soldTexture])

  const soldStageOverlays = useMemo(() => {
    if (!soldStages || activeStage) return []
    const overlays = []
    Object.entries(soldStages).forEach(([stageKey, isSold]) => {
      if (!isSold) return
      const bounds = stageBounds.get(stageKey)
      if (!bounds) return
      const width = bounds.maxX - bounds.minX
      const height = bounds.maxY - bounds.minY
      if (width <= 0 || height <= 0) return
      const centerX = bounds.minX + width / 2
      const centerY = bounds.minY + height / 2
      overlays.push({
        stageKey,
        center: [centerX, centerY],
        width,
        height
      })
    })
    return overlays
  }, [soldStages, stageBounds, activeStage])

  const ref = useRef()
  const meshListRef = useRef([])
  const [bgSize, setBgSize] = useState(null)
  const [bgCenter, setBgCenter] = useState(null)
  const [bgMin, setBgMin] = useState(null)
  const [svgViewBox, setSvgViewBox] = useState(null) // { minX, minY, width, height }

  // Expose the region -> shape map and current SVG viewBox to parent for lot thumbnails
  useEffect(() => {
    if (typeof onShapesMap === 'function') {
      onShapesMap(regionShapeMap, svgViewBox)
    }
  }, [onShapesMap, regionShapeMap, svgViewBox])
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const { camera, size, invalidate, gl, scene, raycaster } = useThree()
  const animationRef = useRef()
  const [isAnimating, setIsAnimating] = useState(false)
  const composerRef = useRef(null)
  const readyRef = useRef(false)


  // Log background and overlay file names for first 30 seconds
  useEffect(() => {
    const startTime = Date.now()
    const logInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      if (elapsed > 30000) { // 30 seconds
        clearInterval(logInterval)
        return
      }
      
      // Extract file names from URLs
      const backgroundFileName = backgroundUrl ? backgroundUrl.split('/').pop() : 'None'
      const overlayFileName = url ? url.split('/').pop() : 'None'
      
    }, 1000) // Log every second
    
    return () => clearInterval(logInterval)
  }, [backgroundUrl, url])

  // Notify parent when initial assets are ready so it can hide the loader
  useEffect(() => {
    if (readyRef.current) return
    const svgLoaded = Array.isArray(paths) && paths.length > 0
    const bgLoaded = !backgroundUrl || Boolean(backgroundTexture || overlaySvg || overlayTexture)
    if (svgLoaded && bgLoaded) {
      readyRef.current = true
      // Delay to next frame to ensure first render paints
      requestAnimationFrame(() => {
        if (typeof onReady === 'function') onReady()
      })
    }
  }, [paths, backgroundUrl, backgroundTexture, overlaySvg, overlayTexture, onReady])

  // Render with blur composer: blur layer 0, overlay layer 1 sharp
  useFrame((state) => {
    const composer = composerRef.current
    const { gl, scene, camera } = state
    // Render normally (no blur) to keep background crisp even when a stage is active
    camera.layers.enable(0); camera.layers.enable(1)
    gl.render(scene, camera)
  })

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mq.matches)
    const handler = (e) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  useLayoutEffect(() => {
    // Keep original SVG coordinate space. Do not recenter the group.
    if (ref.current) ref.current.position.set(0, 0, 0)
    if (process.env.NODE_ENV !== 'production') {
    }
  }, [])

  const register = (idx, mesh, targetOpacity, stageKey) => {
    meshListRef.current[idx] = { mesh, targetOpacity, stageKey }
  }

  // Smooth camera animation function
  const animateCamera = useCallback((targetPosition, targetZoom, targetControls) => {
    if (prefersReducedMotion) {
      // Skip animation if user prefers reduced motion
      camera.position.set(targetPosition.x, targetPosition.y, targetPosition.z)
      camera.zoom = targetZoom
      camera.updateProjectionMatrix()
      if (mapControlsRef && mapControlsRef.current && targetControls) {
        const controls = mapControlsRef.current
        if (controls.target) {
          controls.target.set(targetControls.x, targetControls.y, 0)
          controls.update()
        }
      }
      invalidate()
      return
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    const startPosition = camera.position.clone()
    const startZoom = camera.zoom
    const startControls = mapControlsRef?.current?.target ? mapControlsRef.current.target.clone() : new THREE.Vector3(0, 0, 0)
    
    const startTime = performance.now()
    const duration = 1000 // 1 second animation

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Use easeOutCubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3)
      
      // Interpolate position
      camera.position.lerpVectors(startPosition, targetPosition, easeProgress)
      
      // Interpolate zoom
      camera.zoom = startZoom + (targetZoom - startZoom) * easeProgress
      camera.updateProjectionMatrix()
      
      // Interpolate controls target
      if (mapControlsRef && mapControlsRef.current && targetControls) {
        const controls = mapControlsRef.current
        if (controls.target) {
          controls.target.lerpVectors(startControls, targetControls, easeProgress)
          controls.update()
        }
      }
      
      invalidate()
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setIsAnimating(false)
      }
    }
    
    setIsAnimating(true)
    animationRef.current = requestAnimationFrame(animate)
  }, [prefersReducedMotion, camera, mapControlsRef, invalidate])

  // Auto-zoom/pan to the active stage bounds with smooth animation
  useEffect(() => {
    if (activeStage) {
      // Skip stage zoom if a region is selected (let the selectedRegion effect handle zoom)
      // When selectedRegion becomes null (back button), this will trigger and zoom to stage
      if (selectedRegion) return
      
      // Zoom to specific stage
      const stageMeshes = meshListRef.current
        .filter(entry => entry && entry.mesh && entry.stageKey === activeStage)
        .map(entry => entry.mesh)
      if (!stageMeshes.length) return
      const box = new THREE.Box3()
      stageMeshes.forEach(m => {
        m.updateWorldMatrix(true, false)
        box.expandByObject(m)
      })
      if (box.isEmpty()) return
      const center = new THREE.Vector3()
      box.getCenter(center)
      const sizeVec = new THREE.Vector3()
      box.getSize(sizeVec)
      
      const targetPosition = new THREE.Vector3(center.x, center.y, camera.position.z)
      const padding = 1.0
      const fitX = size.width / (sizeVec.x * padding)
      const fitY = size.height / (sizeVec.y * padding)
      const targetZoom = Math.max(0.1, Math.min(fitX, fitY))
      const targetControls = new THREE.Vector3(center.x, center.y, 0)
      
      animateCamera(targetPosition, targetZoom, targetControls)
    } else {
      // Zoom out to show all stages
      const allMeshes = meshListRef.current
        .filter(entry => entry && entry.mesh)
        .map(entry => entry.mesh)
      if (!allMeshes.length) return
      
      const box = new THREE.Box3()
      allMeshes.forEach(m => {
        m.updateWorldMatrix(true, false)
        box.expandByObject(m)
      })
      if (box.isEmpty()) return
      
      const center = new THREE.Vector3()
      box.getCenter(center)
      const sizeVec = new THREE.Vector3()
      box.getSize(sizeVec)
      
      const targetPosition = new THREE.Vector3(center.x, center.y, camera.position.z)
      const padding = 1.1 // Moderate padding for overview
      const fitX = size.width / (sizeVec.x * padding)
      const fitY = size.height / (sizeVec.y * padding)
      const targetZoom = Math.max(0.1, Math.min(fitX, fitY))
      const targetControls = new THREE.Vector3(center.x, center.y, 0)
      
      animateCamera(targetPosition, targetZoom, targetControls)
    }
  }, [activeStage, selectedRegion, camera, size, invalidate, animateCamera])

  // Initial zoom to fit the full plan or a specific stage - runs once on mount
  const initialZoomDoneRef = useRef(false)
  useEffect(() => {
    // Only do initial zoom if:
    // 1. We haven't done it yet
    // 2. There's no active stage (so we don't override user selection)
    // 3. We have meshes loaded
    if (!initialZoomDoneRef.current && !activeStage && meshListRef.current.length > 0) {
      // Determine which meshes to include in the initial zoom
      let meshesToZoom = []
      if (initialZoomToStage === null || initialZoomToStage === undefined) {
        // Show full plan - include ALL lots
        meshesToZoom = meshListRef.current
          .filter(entry => entry && entry.mesh)
          .map(entry => entry.mesh)
      } else if (initialZoomToStage === '__ALL_UNLOCKED__') {
        meshesToZoom = meshListRef.current
          .filter(entry => {
            if (!entry || !entry.mesh) return false
            // Check if stage is locked - stageLockInfo[entry.stageKey] is an object with { isLocked, displayLabel }
            const stageInfo = stageLockInfo && stageLockInfo[entry.stageKey]
            const isLocked = stageInfo ? (typeof stageInfo === 'object' ? (stageInfo.isLocked || false) : Boolean(stageInfo)) : false
            return !isLocked // Include only unlocked stages
          })
          .map(entry => entry.mesh)
      } else {
        meshesToZoom = meshListRef.current
          .filter(entry => entry && entry.mesh && entry.stageKey === initialZoomToStage)
          .map(entry => entry.mesh)
      }

      const stageMeshes = meshesToZoom
      if (stageMeshes.length > 0) {
        const box = new THREE.Box3()
        stageMeshes.forEach(m => {
          m.updateWorldMatrix(true, false)
          box.expandByObject(m)
        })
        if (!box.isEmpty()) {
          const center = new THREE.Vector3()
          box.getCenter(center)
          const sizeVec = new THREE.Vector3()
          box.getSize(sizeVec)
          
          const targetPosition = new THREE.Vector3(center.x, center.y, camera.position.z)
          const padding = 1.15 // Initial overview padding for first render
          const fitX = size.width / (sizeVec.x * padding)
          const fitY = size.height / (sizeVec.y * padding)
          const targetZoom = Math.max(0.1, Math.min(fitX, fitY))
          const targetControls = new THREE.Vector3(center.x, center.y, 0)
          
          animateCamera(targetPosition, targetZoom, targetControls)
          initialZoomDoneRef.current = true
        }
      }
    }
  }, [initialZoomToStage, activeStage, camera, size, invalidate, animateCamera, stageLockInfo])

  // Reset initial zoom flag when map changes
  useEffect(() => {
    initialZoomDoneRef.current = false
  }, [url])

  // Zoom to selected lot when a stage is active (with less zoom/more padding)
  const previousSelectedRegionRef = useRef(null)
  useEffect(() => {
    // Only zoom if we have an active stage, a selected region, and the region belongs to the active stage
    if (activeStage && selectedRegion && meshListRef.current.length > 0) {
      // Check if this is a new selection (not just the same region re-selected)
      const isNewSelection = previousSelectedRegionRef.current?.id !== selectedRegion.id
      
      // Check if the selected region belongs to the active stage
      const stageKey = (selectedRegion.name && String(selectedRegion.name).trim()) || 'Ungrouped'
      if (stageKey === activeStage && isNewSelection && regionShapeMap.has(selectedRegion.id)) {
        // Find the mesh for this specific region by matching region ID
        let regionMesh = null
        for (let idx = 0; idx < meshListRef.current.length; idx++) {
          const entry = meshListRef.current[idx]
          if (!entry || !entry.mesh) continue
          const entryRegion = getRegionForIndex(idx)
          if (entryRegion && entryRegion.id === selectedRegion.id) {
            regionMesh = entry
            break
          }
        }
        
        if (regionMesh && regionMesh.mesh) {
          const box = new THREE.Box3()
          regionMesh.mesh.updateWorldMatrix(true, false)
          box.expandByObject(regionMesh.mesh)
          
          if (!box.isEmpty()) {
            const center = new THREE.Vector3()
            box.getCenter(center)
            const sizeVec = new THREE.Vector3()
            box.getSize(sizeVec)
            
            const targetPosition = new THREE.Vector3(center.x, center.y, camera.position.z)
            // Use more padding (larger value) to zoom out more - less aggressive zoom
            const padding = 2.0 // Less zoom/more padding than stage zoom (1.25)
            const fitX = size.width / (sizeVec.x * padding)
            const fitY = size.height / (sizeVec.y * padding)
            const targetZoom = Math.max(0.1, Math.min(fitX, fitY))
            const targetControls = new THREE.Vector3(center.x, center.y, 0)
            
            animateCamera(targetPosition, targetZoom, targetControls)
          }
        }
      }
      previousSelectedRegionRef.current = selectedRegion
    } else if (!selectedRegion) {
      previousSelectedRegionRef.current = null
    }
  }, [selectedRegion, activeStage, camera, size, invalidate, animateCamera, regionShapeMap])

  // Compute background plane size/center from meshes when available
  useEffect(() => {
    if (svgViewBox) return // prefer precise viewBox if available
    const entries = meshListRef.current.filter(e => e && e.mesh)
    if (!entries.length) return
    const box = new THREE.Box3()
    entries.forEach(entry => {
      entry.mesh.updateWorldMatrix(true, false)
      box.expandByObject(entry.mesh)
    })
    if (box.isEmpty()) return
    const center = new THREE.Vector3()
    const sizeVec = new THREE.Vector3()
    box.getCenter(center)
    box.getSize(sizeVec)
    setBgCenter(center)
    setBgSize([sizeVec.x, sizeVec.y])
    setBgMin([box.min.x, box.min.y])
    if (process.env.NODE_ENV !== 'production') {
    }
  }, [enriched])

  // Parse the SVG viewBox to align exactly to the original artboard
  useEffect(() => {
    let aborted = false
    const load = async () => {
      try {
        const res = await fetch(url)
        const text = await res.text()
        const m = text.match(/viewBox\s*=\s*"([\d\.\-]+)\s+([\d\.\-]+)\s+([\d\.\-]+)\s+([\d\.\-]+)"/i)
        if (m) {
          const vb = { minX: parseFloat(m[1]), minY: parseFloat(m[2]), width: parseFloat(m[3]), height: parseFloat(m[4]) }
          if (!aborted) {
            setSvgViewBox(vb)
            setBgMin([vb.minX, vb.minY])
            setBgSize([vb.width, vb.height])
            setBgCenter(new THREE.Vector3(vb.minX + vb.width / 2, vb.minY + vb.height / 2, 0))
            if (process.env.NODE_ENV !== 'production') {
            }
          }
        }
      } catch (_) {}
    }
    load()
    return () => { aborted = true }
  }, [url])

  // Improve background texture clarity
  useEffect(() => {
    if (!backgroundTexture) return
    backgroundTexture.anisotropy = 8
    backgroundTexture.wrapS = THREE.ClampToEdgeWrapping
    backgroundTexture.wrapT = THREE.ClampToEdgeWrapping
    backgroundTexture.needsUpdate = true
  }, [backgroundTexture])

  // Enhance overlay texture quality and vibrancy
  useEffect(() => {
    if (!overlayTexture) return
    overlayTexture.anisotropy = 8
    overlayTexture.wrapS = THREE.ClampToEdgeWrapping
    overlayTexture.wrapT = THREE.ClampToEdgeWrapping
    overlayTexture.colorSpace = THREE.SRGBColorSpace
    overlayTexture.needsUpdate = true
  }, [overlayTexture])

  // Ensure raycaster can hit both layers for hover/select when using blur layers
  useEffect(() => {
    if (!raycaster) return
    raycaster.layers.enable(0)
    raycaster.layers.enable(1)
  }, [raycaster])

  // Notify parent component of shape data when hovered region changes
  useEffect(() => {
    if (onShapeData && hoveredRegion && regionShapeMap.has(hoveredRegion.id)) {
      const shape = regionShapeMap.get(hoveredRegion.id)
      onShapeData(hoveredRegion, shape, svgViewBox)
    } else if (onShapeData) {
      onShapeData(null, null, null)
    }
  }, [hoveredRegion, regionShapeMap, svgViewBox, onShapeData])

  useEffect(() => {
    if (!bgMin || !bgSize) return
    if (process.env.NODE_ENV !== 'production') {
    }
  }, [bgMin, bgSize])

  // Postprocessing: blur non-selected layers (layer 0), then overlay sharp layer 1
  useEffect(() => {
    const composer = new EffectComposer(gl)
    const renderPass = new RenderPass(scene, camera)
    const hBlur = new ShaderPass(HorizontalBlurShader)
    const vBlur = new ShaderPass(VerticalBlurShader)
    // mild blur tuned to size; increase factor for stronger
    hBlur.uniforms.h.value = 2 / size.width
    vBlur.uniforms.v.value = 2 / size.height
    composer.addPass(renderPass)
    composer.addPass(hBlur)
    composer.addPass(vBlur)
    composer.setSize(size.width, size.height)
    composerRef.current = composer
    return () => {
      try {
        // Dispose individual passes if supported
        if (typeof renderPass.dispose === 'function') renderPass.dispose()
        if (typeof hBlur.dispose === 'function') hBlur.dispose()
        if (typeof vBlur.dispose === 'function') vBlur.dispose()
        // Dispose composer or its internal render targets depending on availability
        if (composer && typeof composer.dispose === 'function') {
          composer.dispose()
        } else {
          if (composer && composer.renderTarget1 && typeof composer.renderTarget1.dispose === 'function') {
            composer.renderTarget1.dispose()
          }
          if (composer && composer.renderTarget2 && typeof composer.renderTarget2.dispose === 'function') {
            composer.renderTarget2.dispose()
          }
        }
      } catch (_) {}
      composerRef.current = null
    }
  }, [gl, scene, camera, size])

  // Update mesh layers based on activeStage
  useEffect(() => {
    meshListRef.current.forEach(entry => {
      if (!entry || !entry.mesh) return
      const isSelectedStage = activeStage ? (entry.stageKey === activeStage) : false
      // Keep selected stage sharp on layer 1; others (and background) on layer 0
      entry.mesh.layers.set(isSelectedStage ? 1 : 0)
    })
  }, [activeStage, enriched])

  // Ensure background planes are on layer 0 always using onUpdate

  return (
    <group ref={ref} scale={[1, -1, 1]}>
      {/* Optional overlay for pre-rendered lot labels/values */}
      {overlaySvg && bgMin && bgSize && (
        <group 
          position={[0,0,0.05]}
          onUpdate={(group) => {
            group.traverse((child) => {
              if (child.layers) {
                child.layers.set(1)
              }
            })
          }}
        >
          {overlaySvg.paths
            .flatMap((p) => p.toShapes(true).map((shape) => ({ shape, color: p.color, fillOpacity: p.userData?.style?.fillOpacity })))
            .map((s, i) => (
              <mesh
                key={`bg-${i}`}
                renderOrder={100}
                onUpdate={(mesh) => {
                  mesh.layers.set(1) // Always on layer 1 to stay sharp when stage is active
                }}
                ref={(mesh) => {
                  if (mesh && !mesh.userData.overlay) {
                    mesh.userData.overlay = true
                    mesh.raycast = () => null
                    mesh.layers.set(1) // Set to layer 1 to stay sharp when stage is active
                  }
                }}
              >
                <meshBasicMaterial
                  color={s.color || '#ffffff'}
                  opacity={1.0}
                  depthWrite={false}
                  depthTest={false}
                  transparent={true}
                  toneMapped={false}
                  blending={THREE.NormalBlending}
                />
              <shapeBufferGeometry args={[s.shape]} />
            </mesh>
          ))}
        </group>
      )}

      {overlayTexture && bgMin && bgSize && (
        <mesh
          position={[bgMin[0] + bgSize[0] / 2, bgMin[1] + bgSize[1] / 2, 0.05]}
          scale={[1, -1, 1]}
          renderOrder={100}
          onUpdate={(mesh) => {
            mesh.layers.set(1) // Always on layer 1 to stay sharp when stage is active
          }}
          ref={(mesh) => {
            if (mesh && !mesh.userData.overlayTexture) {
              mesh.userData.overlayTexture = true
              mesh.raycast = () => null
              mesh.layers.set(1) // Set to layer 1 to stay sharp when stage is active
            }
          }}
        >
          <planeGeometry args={[bgSize[0], bgSize[1]]} />
          <meshBasicMaterial
            map={overlayTexture}
            opacity={1.0}
            transparent={true}
            depthWrite={false}
            depthTest={false}
            toneMapped={false}
            blending={THREE.NormalBlending}
          />
        </mesh>
      )}

      {backgroundTexture && bgSize && bgCenter && bgMin && (
        <mesh position={[bgMin[0] + bgSize[0] / 2, bgMin[1] + bgSize[1] / 2, -0.01]} scale={[1, -1, 1]}
          onUpdate={(m)=>{}}
        > 
          <planeGeometry args={[bgSize[0], bgSize[1]]} />
          <meshBasicMaterial map={backgroundTexture} depthWrite={true} transparent={false} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      )}
      
      {/* Fallback background for testing - remove this later */}
      {!backgroundTexture && (
        <mesh position={[0, 0, -0.01]} scale={[1, -1, 1]}>
          <planeGeometry args={[100, 100]} />
          <meshBasicMaterial color="#f0f0f0" depthWrite={true} transparent={false} side={THREE.DoubleSide} />
        </mesh>
      )}

      {!activeStage && soldTexture && soldStageOverlays.length > 0 && (
        soldStageOverlays.map(({ stageKey, center, width, height }) => {
          const maxWidth = width * 0.75
          let overlayWidth = Math.max(Math.min(width, height) * 0.4, Math.min(maxWidth, width))
          let overlayHeight = overlayWidth / soldTextureAspect
          const maxHeight = height * 0.6
          if (overlayHeight > maxHeight) {
            overlayHeight = maxHeight
            overlayWidth = overlayHeight * soldTextureAspect
          }
          const rotation = -0.2
          const titleText = stageKey ? `Stage - ${stageKey}` : 'Stage'
          const subtitleText = 'Next release coming soon'
          const minDimension = Math.min(overlayWidth, overlayHeight)
          const titleFontSize = minDimension * 0.12
          const subtitleFontSize = minDimension * 0.12
          const titleOffset = overlayHeight * 0.35
          const subtitleOffset = overlayHeight * 0.35
          const horizontalOffset = 0
          return (
            <group
              key={`sold-overlay-${stageKey}`}
              position={[center[0], center[1], 5]}
              rotation={[0, 0, rotation]}
              renderOrder={1000}
              raycast={() => null}
              onUpdate={(m) => m.layers.set(1)}
            >
              <mesh scale={[1, -1, 1]}>
                <planeGeometry args={[overlayWidth, overlayHeight]} />
                <meshBasicMaterial
                  map={soldTexture}
                  transparent
                  opacity={0.88}
                  depthWrite={false}
                  depthTest={false}
                  toneMapped={false}
                  side={THREE.DoubleSide}
                />
              </mesh>
              <Text
                position={[horizontalOffset, -titleOffset, 0.2]}
                scale={[1, -1, 1]}
                fontSize={titleFontSize}
                color="#b91c1c"
                anchorX="center"
                anchorY="middle"
                font="/fonts/Inter-Bold.woff"
                raycast={() => null}
                toneMapped={false}
              >
                {titleText}
              </Text>
              <Text
                position={[horizontalOffset, subtitleOffset, 0.2]}
                scale={[1, -1, 1]}
                fontSize={subtitleFontSize}
                color="#b91c1c"
                anchorX="center"
                anchorY="middle"
                font="/fonts/Inter-Regular.woff"
                raycast={() => null}
                toneMapped={false}
              >
                {subtitleText}
              </Text>
            </group>
          )
        })
      )}

      {bgMin && bgSize && activeStage && (
        <>
          {/* Brighten slightly */}
          <mesh position={[bgMin[0] + bgSize[0] / 2, bgMin[1] + bgSize[1] / 2, -0.009]} scale={[1, -1, 1]}
            onUpdate={(m)=>{ m.layers.set(0) }}
          >
            <planeGeometry args={[bgSize[0], bgSize[1]]} />
            <meshBasicMaterial color={'#ffffff'} transparent opacity={0.08} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          {/* Add subtle depth/dark to shadows */}
          <mesh position={[bgMin[0] + bgSize[0] / 2, bgMin[1] + bgSize[1] / 2, -0.008]} scale={[1, -1, 1]}
            onUpdate={(m)=>{ m.layers.set(0) }}
          >
            <planeGeometry args={[bgSize[0], bgSize[1]]} />
            <meshBasicMaterial color={'#000000'} transparent opacity={0.06} depthWrite={false} />
          </mesh>
        </>
      )}

      {/* Debug outline of SVG bounds */}
      {bgMin && bgSize && (
        <lineSegments position={[0,0,-0.02]}>
          <bufferGeometry>
            <bufferAttribute
              attachObject={["attributes", "position"]}
              count={8}
              array={new Float32Array([
                bgMin[0], bgMin[1], 0,
                bgMin[0] + bgSize[0], bgMin[1], 0,
                bgMin[0] + bgSize[0], bgMin[1], 0,
                bgMin[0] + bgSize[0], bgMin[1] + bgSize[1], 0,
                bgMin[0] + bgSize[0], bgMin[1] + bgSize[1], 0,
                bgMin[0], bgMin[1] + bgSize[1], 0,
                bgMin[0], bgMin[1] + bgSize[1], 0,
                bgMin[0], bgMin[1], 0
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#ff00ff" linewidth={1} />
        </lineSegments>
      )}
      {enriched.map((props, idx) => (
        <Cell 
          key={props.shape.uuid} 
          baseColor={props.baseColor} 
          shape={props.shape} 
          fillOpacity={props.fillOpacity} 
          onHover={onHover}
          onSelect={onSelect} 
          region={props.region}
          isHighlighted={
            // Highlight if selected lot OR (no active stage and its stage is hovered)
            (selectedRegion && props.region && selectedRegion.id === props.region.id) ||
            (!activeStage && hoveredStage && props.stageKey && hoveredStage === props.stageKey)
          }
          isHovered={
            // Show lot-level hover only when a specific lot is hovered on the map
            Boolean(
              activeStage && hoveredRegion && props.region && hoveredRegion.id === props.region.id
            )
          }
          isDimmed={Boolean(activeStage && props.stageKey !== activeStage)}
          isStageActive={Boolean(activeStage)}
          index={idx}
          register={(i, m, o) => register(i, m, o, props.stageKey)}
          isLocked={props.isLocked}
          isAdminMode={isAdminMode}
          activeTab={activeTab}
          activeStage={activeStage}
          hoveredGroup={hoveredGroup}
          hoveredRegion={hoveredRegion}
          selectedGroup={selectedGroup}
        />
      ))}
    </group>
  )
}


